/*
 * Choosing people: a filter, a list of check boxes, and a count of what is ticked.
 *
 * Only accounts a group may actually hold are offered — live humans, minus whoever is in the group
 * already (`isMentionable`, spec 01 §11): a picker that offered a bot would be offering a refusal.
 * Somebody whose invitation nobody has answered yet is a legal member of a group and is offered, with
 * the *Invited* pill the chip draws for them wherever a person is drawn (`PersonChip.tsx`): what the
 * reader needs is not a row fewer but the reason a mention of this group may go half unread.
 * The list is a plain group of check boxes rather than a listbox widget, because that is the control
 * a browser and a screen reader already agree about, and every row is a real label.
 *
 * The group has a name and no frame of its own (X-B1 D6, VISUAL.md §7.18). It used to be a
 * `<fieldset>` with a `<legend>`, which drew a second box with a floating caption inside the dialog's
 * own frame — two frames around one list, and a caption repeating the dialog's title. What a screen
 * reader needs is the name, kept as a hidden span the group is labelled by; what a sighted reader
 * needs is the boundary of the list, which the list's own ground and radius already draw.
 *
 * The count belongs to the picker: one line, under the list it counts. A caller that printed its own
 * as well said "0 selected" twice under one list — the same sentence, from somebody who could not see
 * that it had been said.
 */

import { useId, useMemo, useState } from "react"
import { Field, ListState } from "../../ui/index.ts"
import { t } from "../i18n/index.ts"
import "../people/people.css"
import { byName, isMentionable, matchesPerson } from "../people/person.ts"
import { chipPerson, PersonChip } from "../people/PersonChip.tsx"
import { useModel } from "../shell/index.ts"
import type { Model, PublicUser, UserId } from "../store/model.ts"

interface PeoplePickerProps {
	/** Accounts already in the group; they are not offered again. */
	exclude: UserId[]
	selected: UserId[]
	onChange: (selected: UserId[]) => void
	/** Names the group of check boxes for a screen reader; it is not drawn. */
	legend: string
}

/**
 * Who a picker may offer: live humans the caller does not already hold, by name. It is exported
 * because a caller needs the *count* before it draws anything around the list — a dialog whose picker
 * has nobody left to offer must not keep the controls that act on a choice nobody can make
 * (N5-R1-10) — and two answers to "who can be added" would be one too many.
 */
export function pickableFor(users: Model["users"], exclude: readonly UserId[]): PublicUser[] {
	const taken = new Set(exclude)
	return byName(Object.values(users).filter((u) => isMentionable(u) && !taken.has(u.id)))
}

export function PeoplePicker({ exclude, selected, onChange, legend }: PeoplePickerProps) {
	const users = useModel((model) => model.users)
	const [query, setQuery] = useState("")
	const legendId = useId()

	const candidates = useMemo(() => pickableFor(users, exclude), [users, exclude])
	const shown = candidates.filter((user) => matchesPerson(user, query))

	function toggle(id: UserId, on: boolean) {
		onChange(on ? [...selected, id] : selected.filter((other) => other !== id))
	}

	if (candidates.length === 0) {
		return <ListState.Empty title={t("groups.picker.empty")} />
	}

	return (
		<div className="tb-form" role="group" aria-labelledby={legendId}>
			<span className="tb-visually-hidden" id={legendId}>
				{legend}
			</span>
			<Field label={t("groups.picker.label")}>
				{(control) => (
					<input
						{...control}
						type="search"
						value={query}
						placeholder={t("groups.picker.placeholder")}
						onChange={(event) => setQuery(event.target.value)}
					/>
				)}
			</Field>
			<ul className="tb-picker__list">
				{shown.map((user) => (
					<li key={user.id}>
						<label className="tb-picker__option">
							<input
								type="checkbox"
								checked={selected.includes(user.id)}
								onChange={(event) => toggle(user.id, event.target.checked)}
							/>
							{/* The same chip every other list of people draws, with neither opener: a row
							    inside this dialog is a check box and nothing else. */}
							<PersonChip person={chipPerson(user)} hover={false} handle />
						</label>
					</li>
				))}
			</ul>
			<p className="tb-person__meta">{t("groups.picker.selected", { count: selected.length })}</p>
		</div>
	)
}
